<script setup lang="ts">
import { gsap } from 'gsap'

const { t } = useI18n()
const section = ref<HTMLElement | null>(null)
const dirX = useDirX()

let mm: ReturnType<typeof gsap.matchMedia> | null = null

// Real confetti emitter: spawns DOM nodes that explode outward, fall with
// gravity, rotate, and fade. Pieces are removed after their tween finishes.
function fireConfetti(container: HTMLElement) {
  const colors = ['#FDC02D', '#CB2097', '#2E83FB', '#111827', '#FF6B9D']
  const COUNT = 70
  for (let i = 0; i < COUNT; i++) {
    const piece = document.createElement('span')
    piece.className = 'confetti-piece'
    const color = colors[Math.floor(Math.random() * colors.length)]
    const shape = i % 3 // 0=square, 1=circle, 2=rect
    const size = 5 + Math.random() * 7
    piece.style.background = color
    piece.style.width = `${size}px`
    piece.style.height = shape === 2 ? `${size * 0.4}px` : `${size}px`
    piece.style.borderRadius = shape === 1 ? '50%' : '2px'
    container.appendChild(piece)

    const angle = (Math.PI * 2 * i) / COUNT + (Math.random() - 0.5) * 0.4
    const velocity = 90 + Math.random() * 140
    const x = Math.cos(angle) * velocity
    const yPeak = Math.sin(angle) * velocity * 0.55 - 40
    const yFinal = 180 + Math.random() * 120

    const tl = gsap.timeline({ onComplete: () => piece.remove() })
    tl.set(piece, { x: 0, y: 0, opacity: 1, scale: 0.6 })
      .to(piece, { scale: 1, duration: 0.18, ease: 'back.out(2)' }, 0)
      .to(piece, { x: x * 0.45, y: yPeak, duration: 0.45, ease: 'power2.out' }, 0)
      .to(piece, { x, y: yFinal, duration: 1.45, ease: 'power2.in' }, 0.45)
      .to(piece, { rotation: (Math.random() - 0.5) * 900, duration: 1.9, ease: 'none' }, 0)
      .to(piece, { opacity: 0, duration: 0.6, ease: 'power2.in' }, 1.25)
  }
}

onMounted(() => {
  const el = section.value
  if (!el) return

  const dir = dirX.value
  gsap.set(el.querySelector('.why-mgmt-message'), { opacity: 0, x: -10 * dir, scale: 0.85 })

  // Heading block slide from start
  gsap.fromTo(el.querySelector('.why-heading'),
    { opacity: 0, x: -60 * dir, y: 20 },
    {
      opacity: 1, x: 0, y: 0, duration: 0.95, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 80%', once: true },
    },
  )

  // Large "Grow" card sweeps up — scrubbed so it reverses when scrolling back
  gsap.fromTo(el.querySelector('.why-grow-card'),
    { y: 80, scale: 0.93 },
    {
      y: 0, scale: 1, ease: 'power4.out',
      scrollTrigger: { trigger: el.querySelector('.why-grow-card'), start: 'top 85%', end: 'top 30%', scrub: 1.2 },
    },
  )

  // Arrow flips horizontally in RTL. Driven via GSAP rather than a `rtl:` CSS
  // class because the parallax tween below writes `transform`, which would wipe
  // a CSS scaleX. Setting it through GSAP keeps it in the transform cache so the
  // parallax preserves the flip (and it stays flipped on mobile where there's
  // no parallax).
  gsap.set(el.querySelector('.why-arrow-grow'), { scaleX: dir })

  // The decorative parallax sweeps are tuned for the absolute positions these
  // elements occupy on larger screens. On mobile they're shown smaller and
  // static (see template) so they don't fly across the cramped layout.
  mm = gsap.matchMedia()

  // Parallax: arrow inside the grow box drifts upward (md+)
  mm.add('(min-width: 768px)', () => {
    gsap.to(el.querySelector('.why-arrow-grow'), {
      y: -60, ease: 'none',
      scrollTrigger: {
        trigger: el.querySelector('.why-grow-card'),
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    })
  })

  // Parallax: bookings card emerges from behind the grow card (z-5 sits below
  // the grow card's z-10) and rises ~300px as you scroll (xl+, where it floats
  // above-right of the grow card).
  mm.add('(min-width: 1280px)', () => {
    gsap.fromTo(el.querySelector('.why-bookings-card'),
      { y: 220 },
      {
        y: -80, ease: 'none',
        scrollTrigger: {
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      },
    )
  })

  // Bottom two cards stagger up
  gsap.fromTo(el.querySelectorAll('.why-bottom-card'),
    { opacity: 0, y: 70, scale: 0.9 },
    {
      opacity: 1, y: 0, scale: 1, duration: 0.85, ease: 'back.out(1.5)', stagger: 0.15,
      scrollTrigger: { trigger: el.querySelector('.why-bottom-row'), start: 'top 88%', once: true },
    },
  )

  const confettiHost = el.querySelector<HTMLElement>('.why-confetti-host')

  // Desktop (lg+): scrub-driven so the revenue fill and management boxes track
  // scroll precisely and reverse on scroll-up. Confetti fires once per forward
  // pass (progress ≥ 95%) and re-arms when you scroll back below 80%.
  mm.add('(min-width: 1024px)', () => {
    let _confettiDone = false
    gsap.timeline({
      scrollTrigger: {
        trigger: el.querySelector('.why-progress-fill'),
        start: 'top 85%',
        end: 'bottom 40%',
        scrub: 1.2,
        onUpdate(self) {
          if (self.progress < 0.8) _confettiDone = false
          if (self.progress >= 0.95 && !_confettiDone && confettiHost) {
            _confettiDone = true
            fireConfetti(confettiHost)
          }
        },
      },
    })
      .fromTo(el.querySelector('.why-progress-fill'),
        { width: '0%' },
        { width: '100%', duration: 1, ease: 'none' }, 0)
      .to(el.querySelector('.why-progress-target'),
        { scale: 1.35, duration: 0.15, ease: 'back.out(2)' }, 0.9)
      .to(el.querySelector('.why-progress-target'),
        { scale: 1, duration: 0.15, ease: 'power2.inOut' }, 1.05)

    gsap.timeline({
      scrollTrigger: { trigger: el.querySelector('.why-mgmt-row'), start: 'top 85%', end: 'bottom 50%', scrub: 1.2 },
    })
      .to(el.querySelectorAll('.why-mgmt-box'), {
        backgroundColor: '#FDC02D',
        duration: 0.4,
        ease: 'power2.out',
        stagger: 0.3,
      })
      .to(el.querySelector('.why-mgmt-message'),
        { opacity: 1, x: 0, scale: 1, duration: 0.5, ease: 'power2.out' }, '+=0.1')
  })

  // Mobile/tablet (<lg): touch users flick-scroll, which makes a scrubbed fill
  // snap straight to 100% so they only ever catch the confetti. Play it as a
  // timed sequence on enter instead, so the bar visibly fills and the boxes
  // light up while the card sits in view, with confetti as the payoff.
  mm.add('(max-width: 1023px)', () => {
    gsap.timeline({
      scrollTrigger: {
        trigger: el.querySelector('.why-progress-fill'),
        start: 'top 80%',
        // Replay on every entry (and reset when scrolled back above) instead of
        // firing once, so it repeats like the scrubbed desktop version.
        toggleActions: 'restart none none reset',
      },
    })
      .fromTo(el.querySelector('.why-progress-fill'),
        { width: '0%' },
        {
          width: '100%', duration: 1.6, ease: 'power1.inOut',
          onComplete: () => { if (confettiHost) fireConfetti(confettiHost) },
        })
      .to(el.querySelector('.why-progress-target'),
        { scale: 1.35, duration: 0.18, ease: 'back.out(2)' }, '>-0.1')
      .to(el.querySelector('.why-progress-target'),
        { scale: 1, duration: 0.18, ease: 'power2.inOut' })

    gsap.timeline({
      scrollTrigger: {
        trigger: el.querySelector('.why-mgmt-row'),
        start: 'top 80%',
        toggleActions: 'restart none none reset',
      },
    })
      .to(el.querySelectorAll('.why-mgmt-box'), {
        backgroundColor: '#FDC02D',
        duration: 0.45,
        ease: 'power2.out',
        stagger: 0.35,
      })
      .to(el.querySelector('.why-mgmt-message'),
        { opacity: 1, x: 0, scale: 1, duration: 0.5, ease: 'power2.out' }, '+=0.15')
  })

  // CTA
  gsap.fromTo(el.querySelector('.why-cta'),
    { opacity: 0, y: 30, scale: 0.9 },
    {
      opacity: 1, y: 0, scale: 1, duration: 0.65, ease: 'back.out(1.4)',
      scrollTrigger: { trigger: el.querySelector('.why-cta'), start: 'top 93%', once: true },
    },
  )
})

onUnmounted(() => {
  mm?.revert()
  mm = null
})
</script>

<template>
  <section id="why" ref="section" class="py-12 md:py-16 lg:py-20 bg-[linear-gradient(89deg,rgba(255,251,235,0.85)_0%,rgba(254,242,242,0.85)_100%)]">
    <div class="container">

      <div class="why-heading relative z-10 mb-8 lg:mb-12 max-w-2xl">
        <i18n-t
          keypath="whyBookforme.heading"
          tag="h2"
          class="text-[clamp(2rem,4vw,3rem)] font-bold leading-[1.25] text-deep-indigo mb-3">
          <template #brand>
            <span class="text-deep-koamaru-950">bookfor</span><span class="text-heart-purple">me</span>
          </template>
        </i18n-t>
        <!-- <i18n-t
          keypath="whyBookforme.subheading"
          tag="h3"
          class="text-2xl md:text-[2rem] font-bold text-gray-900 mb-3 leading-[1.3]">
          <template #headache>
            <span class="text-sky-blue underline underline-offset-4">{{ t('whyBookforme.headache') }}</span>
          </template>
        </i18n-t> -->
        <p class="text-lg md:text-xl text-overcast-night leading-[1.6]">
          {{ t('whyBookforme.body') }}
        </p>
      </div>

      <div class="grid gap-6">
        <div class="relative mb-5">
          <!-- Decorative card -->
          <img src="/illustrations/bookings-card.svg" alt="" aria-hidden="true" class="why-bookings-card block absolute size-44 -top-20 sm:size-65 sm:-top-34 xl:size-80 xl:-top-64 ltr:right-0 rtl:left-0 z-5">
          <!-- Grow Your Audience -->
          <div class="why-grow-card bg-white border-2 border-gray-900 rounded-2xl p-6 md:py-10 md:px-12 shadow-[10px_10px_0_#2E83FB] z-10 relative">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div class="flex gap-5">
                  <div class="size-14 md:size-17 border border-gray-900 rounded flex items-center justify-center shadow-[4px_4px_0_#111827] mb-4 md:mb-4.5 bg-sky-blue text-white">
                    <UIcon name="i-approved-arrow-upscale" class="size-7 md:size-8 text-white" />
                  </div>

                  <div class="text-2xl md:text-3xl lg:text-[2.5rem] font-bold leading-[1.15] text-gray-900 mb-3 md:mb-4">
                    {{ t('whyBookforme.grow_pre') }}<br>
                    <span class="text-sky-blue">{{ t('whyBookforme.grow_colored') }}</span>
                  </div>
                </div>

                <p class="text-base md:text-lg font-medium text-overcast-night leading-[1.7]">{{ t('whyBookforme.grow_desc') }}</p>
              </div>

              <div class="bg-light-breeze border-2 border-gray-900 rounded px-6 py-8 md:px-10 md:py-15 shadow-[4px_4px_0_#111827] relative">
                <img src="/illustrations/arrow-grow.svg" alt="" class="why-arrow-grow block size-48 sm:size-54 md:size-60 lg:size-70 self-center absolute ltr:right-4 rtl:left-4 md:ltr:right-10 md:rtl:left-10 lg:ltr:right-12 lg:rtl:left-16 -bottom-6 z-5" aria-hidden="true">

                <div class="inline-flex items-center gap-2 bg-sky-blue-alt border-2 border-gray-900 rounded px-3 py-2 md:px-5 md:py-2.5 shadow-[4px_4px_0_#111827] text-base md:text-xl font-bold text-gray-900 ltr:-rotate-[5deg] rtl:rotate-[5deg] relative z-10">
                  <img src="/illustrations/women-client.svg" alt="" aria-hidden="true" width="28" height="28" class="w-6 h-6 md:w-7 md:h-7">
                  {{ t('whyBookforme.new_booking') }}
                </div>

                <div class="flex mt-2 relative z-20">
                  <div class="bg-white border-2 border-gray-900 rounded px-3 py-2 md:px-5 md:py-2.5 shadow-[4px_4px_0_#111827] text-sm md:text-lg font-bold flex items-center gap-2 ltr:rotate-[1.3deg] rtl:-rotate-[1.3deg]">
                    <UIcon name="i-approved-star" class="size-4 md:size-5 text-gray-900" /> {{ t('whyBookforme.star_review') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="why-bottom-row grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Maximize Returns -->
          <div class="why-bottom-card bg-white border-2 border-gray-900 rounded-2xl p-6 md:py-10 md:px-12 shadow-[10px_10px_0_#CB2097]">
            <div class="flex gap-5">
              <div class="size-14 md:size-17 border border-gray-900 rounded flex items-center justify-center shadow-[4px_4px_0_#111827] mb-4 md:mb-4.5 bg-heart-purple text-white">
                <UIcon name="i-approved-cash" class="size-7 md:size-8 text-white" />
              </div>
              <div class="text-2xl md:text-3xl lg:text-[2.5rem] font-bold leading-[1.15] text-gray-900 mb-3 md:mb-4">
                {{ t('whyBookforme.maximize_pre_br') }}<br>
                {{ t('whyBookforme.maximize_post_br') }} <span class="text-heart-purple">{{ t('whyBookforme.maximize_colored') }}</span>
              </div>
            </div>
            <p class="text-base md:text-lg font-medium text-overcast-night leading-[1.7]">{{ t('whyBookforme.maximize_desc') }}</p>
            <div class="mt-4">
              <div class="text-xs font-bold text-gray-900 mb-1">{{ t('whyBookforme.revenue_growth') }}</div>
              <div class="flex items-center gap-2 mt-1">
                <div class="flex-1 h-2 bg-white border border-gray-900 rounded-sm overflow-hidden">
                  <div class="why-progress-fill h-full bg-heart-purple" style="width: 0%"/>
                </div>
                <div class="relative inline-flex shrink-0">
                  <!-- Confetti pieces are appended here by fireConfetti(); overflow visible so they can fly past -->
                  <span class="why-confetti-host absolute top-1/2 left-1/2 size-0 pointer-events-none [&>*]:will-change-transform" aria-hidden="true"/>
                  <!-- Trend icon rather than a percentage: there is no measured figure to show -->
                  <span class="why-progress-target inline-flex text-heart-purple" aria-hidden="true">
                    <UIcon name="i-heroicons-arrow-trending-up-20-solid" class="size-5 rtl:-scale-x-100" />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Effortless Management -->
          <div class="why-bottom-card bg-white border-2 border-gray-900 rounded-2xl p-6 md:py-10 md:px-12 shadow-[10px_10px_0_#FDC02D]">
            <div class="flex gap-5">
              <div class="size-14 md:size-17 border border-gray-900 rounded flex items-center justify-center shadow-[4px_4px_0_#111827] mb-4 md:mb-4.5 bg-cheese-yellow text-gray-900">
                <UIcon name="i-approved-group" class="size-7 md:size-8 text-gray-900" />
              </div>
              <div class="text-2xl md:text-3xl lg:text-[2.5rem] font-bold leading-[1.15] text-gray-900 mb-3 md:mb-4">
                {{ t('whyBookforme.management_pre') }}<br>
                <span class="text-cheese-yellow">{{ t('whyBookforme.management_colored') }}</span>
              </div>
            </div>
            <p class="text-base md:text-lg font-medium text-overcast-night leading-[1.7]">{{ t('whyBookforme.management_desc') }}</p>
            <div class="why-mgmt-row flex items-center gap-2 mt-4 flex-wrap">
              <div class="why-mgmt-box h-6 w-14 md:w-17 bg-white border border-gray-900 rounded-sm"/>
              <div class="why-mgmt-box h-6 w-14 md:w-17 bg-white border border-gray-900 rounded-sm"/>
              <div class="why-mgmt-box h-6 w-14 md:w-17 bg-white border border-gray-900 rounded-sm"/>
              <div class="why-mgmt-box h-6 w-14 md:w-17 bg-white border border-gray-900 rounded-sm"/>
              <div class="why-mgmt-message ms-1 inline-flex items-center gap-1.5">
                <UIcon name="i-heroicons-check-circle-solid" class="size-5 text-cheese-yellow" />
                <span class="text-sm font-semibold text-gray-900 whitespace-nowrap">{{ t('whyBookforme.no_time_to_waste') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
